import { useState } from 'react';
import { X, MessageCircle, Mail, Phone, Calendar, FileText, ChevronRight } from 'lucide-react';
import { Link } from 'react-router';

export function ContactWidget() {
  const [isOpen, setIsOpen] = useState(false);

  const contactOptions = [
    {
      icon: Mail,
      title: 'Email Us',
      description: 'Get a response within 24 hours',
      action: 'mailto:info@creabiz.com',
      color: 'from-blue-500 to-blue-600',
      isExternal: true
    },
    {
      icon: Phone,
      title: 'Call Us',
      description: 'Mon-Fri, 9am-6pm EST',
      action: 'tel:+1234567890',
      color: 'from-green-500 to-green-600',
      isExternal: true
    },
    {
      icon: Calendar,
      title: 'Schedule a Call',
      description: 'Book a free consultation',
      action: '/contact',
      color: 'from-purple-500 to-purple-600',
      isExternal: false
    },
    {
      icon: FileText,
      title: 'Get a Quote',
      description: 'Instant project estimate',
      action: '/contact',
      color: 'from-orange-500 to-orange-600',
      isExternal: false
    }
  ];

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* Contact Options Panel */}
      {isOpen && (
        <>
          {/* Backdrop for mobile */}
          <div 
            className="fixed inset-0 bg-black/20 backdrop-blur-sm md:hidden -z-10 animate-in fade-in duration-200"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Options Panel */}
          <div className="absolute bottom-20 right-0 w-80 max-w-[calc(100vw-3rem)] animate-in slide-in-from-bottom-4 fade-in duration-300">
            {/* Outer glow */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#2F2E5C]/30 to-[#D74444]/30 rounded-3xl blur-2xl" />
            
            <div className="relative bg-white/98 backdrop-blur-2xl rounded-3xl shadow-2xl border-2 border-gray-200 overflow-hidden">
              {/* Matte Texture Overlay */}
              <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\' /%3E%3C/svg%3E")'}} />
              
              {/* Header */}
              <div className="relative bg-gradient-to-r from-[#2F2E5C] via-[#3d3b6e] to-[#2F2E5C] px-6 py-4 overflow-hidden">
                {/* Animated orbs */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#D74444]/30 to-transparent rounded-full blur-2xl animate-pulse" style={{ animationDuration: '3s' }} />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-[#E87D3E]/20 to-transparent rounded-full blur-xl animate-pulse" style={{ animationDuration: '4s' }} />
                
                <div className="relative flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-white text-lg">How Can We Help?</h3>
                    <p className="text-xs text-white/90">Choose your preferred contact method</p>
                  </div>
                  
                  <button
                    onClick={() => setIsOpen(false)}
                    className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 backdrop-blur-xl border border-white/20 flex items-center justify-center transition-all group"
                    aria-label="Close contact widget"
                  >
                    <X className="w-4 h-4 text-white group-hover:rotate-90 transition-transform" />
                  </button>
                </div>
              </div>

              {/* Contact Options */}
              <div className="relative p-4 space-y-2">
                {contactOptions.map((option, index) => {
                  const IconComponent = option.icon;
                  const Component = option.isExternal ? 'a' : Link;
                  const props = option.isExternal 
                    ? { href: option.action, target: option.action.startsWith('http') ? '_blank' : undefined, rel: option.action.startsWith('http') ? 'noopener noreferrer' : undefined }
                    : { to: option.action };

                  return (
                    <Component
                      key={index}
                      {...props}
                      onClick={() => setIsOpen(false)}
                      className="group relative block bg-gradient-to-br from-gray-50 to-white rounded-2xl p-4 border-2 border-gray-200 hover:border-[#D74444] transition-all hover:shadow-lg"
                    >
                      {/* Hover glow */}
                      <div className={`absolute inset-0 bg-gradient-to-r ${option.color} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity`} />
                      
                      <div className="relative flex items-center gap-4">
                        {/* Icon */}
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${option.color} flex items-center justify-center text-white shadow-lg flex-shrink-0 group-hover:scale-110 transition-transform`}>
                          <IconComponent className="w-6 h-6" />
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-[#2F2E5C] mb-0.5 group-hover:text-[#D74444] transition-colors">
                            {option.title}
                          </h4>
                          <p className="text-xs text-gray-600">
                            {option.description}
                          </p>
                        </div>
                        
                        {/* Arrow */}
                        <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#D74444] group-hover:translate-x-1 transition-all flex-shrink-0" />
                      </div>
                    </Component>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="relative px-6 py-4 bg-gradient-to-br from-gray-50 to-white border-t-2 border-gray-200">
                <p className="text-xs text-gray-600 text-center">
                  Available Mon-Fri, 9am-6pm EST
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="group relative w-16 h-16 transition-all"
        aria-label="Open contact widget"
      >
        {/* Outer glow - animated pulse */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#D74444] to-[#E87D3E] rounded-full blur-xl opacity-50 group-hover:opacity-75 animate-pulse transition-opacity" style={{ animationDuration: '2s' }} />
        
        {/* Button background with gradient */}
        <div className={`relative w-full h-full bg-gradient-to-br from-[#D74444] to-[#E87D3E] rounded-full flex items-center justify-center shadow-2xl transition-all ${
          isOpen ? 'rotate-0 scale-95' : 'group-hover:scale-110 group-hover:rotate-12'
        }`}>
          {/* Icon */}
          <div className={`transition-all duration-300 ${isOpen ? 'rotate-90 scale-0 opacity-0' : 'rotate-0 scale-100 opacity-100'}`}>
            <MessageCircle className="w-7 h-7 text-white" />
          </div>
          
          <div className={`absolute transition-all duration-300 ${isOpen ? 'rotate-0 scale-100 opacity-100' : 'rotate-90 scale-0 opacity-0'}`}>
            <X className="w-7 h-7 text-white" />
          </div>
          
          {/* Notification badge (optional - can be enabled based on unread messages) */}
          {!isOpen && (
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 border-2 border-white rounded-full flex items-center justify-center">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
            </div>
          )}
        </div>

        {/* Tooltip on hover (desktop only) */}
        {!isOpen && (
          <div className="absolute right-full mr-4 top-1/2 -translate-y-1/2 hidden lg:block opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            <div className="relative">
              {/* Arrow */}
              <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full w-2 h-2 bg-[#2F2E5C] rotate-45" />
              
              <div className="bg-[#2F2E5C] text-white px-4 py-2 rounded-lg whitespace-nowrap text-sm font-semibold shadow-xl">
                Need help? Let's talk!
              </div>
            </div>
          </div>
        )}
      </button>
    </div>
  );
}
